import React, { useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCIcon, RCCInput, RCCTooltip } from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import AppIcons from "src/components/AppIcons";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import UserColumn from "src/components/DefaultColumnsList/User";
import FiltersContainer from "src/components/FiltersContainer";
import LovBusinessGroups from "src/components/Lov/BusinessGroups";
import LovUsers from "src/components/Lov/Users";
import ModalDescription from "src/components/ModalDescription";
import { IRefDescriptionModal } from "src/components/ModalDescription/interface";
import PageTitle from "src/components/PageTitle";
import Pagination from "src/components/Pagination";
import { useTranslation } from "src/context/Translations/context";
import formatDate from "src/utils/formatDate";
import { IFiltersIncidents } from "./interface";
import OverflowSpan from "./styles";

const Incidents: React.FC = () => {

	const paginationRef = useRef<IForwardPagination>(null);
	const modalDescriptionRef = useRef<IRefDescriptionModal>(null);
  
	const { translate, currentLanguage } = useTranslation();
	const { 
		handleSubmit : handleSubmitFilters, 
		control: controlFilters, 
		register : registerFilters, 
		reset: resetFilters, 
		watch: watchFilters ,
		setValue: setValueFilters

	} = useForm();

	const [filters, setFilters] = useState<IFiltersIncidents>(
		{ filter: "", group: "", user: "", startDate: null, endDate: null }
	);
	const [showFilters, setShowFilters] = useState(false);

	const submitFilters = (data: any) => {
		setFilters({ 
			filter: data.filter ,
			group: data.group?.value || data.group || "",
			user: data.user?.value || data.user || "",
			startDate:  data.startDate ? formatDate(data.startDate) : null,
			endDate: data.endDate ? formatDate(data.endDate) : null
		});
		paginationRef.current?.reload();
	};

	const colMessage = (row: any) => (
		<>
    	<RCCGrid
				container
				item
				justifyContent="space-between"
				wrap="nowrap"
				alignItems="center"
			>
				<RCCIcon 
					name="infoCircle" 
					color="var(--grey)" 
					style={{ cursor: "pointer", marginRight: "8px"}}
					size={16}
					onClick={() => modalDescriptionRef.current?.openModal(translate("Mensagem"), row.efs005Mensagem)} 
				/>
				<OverflowSpan>
					{row.efs005Mensagem}
				</OverflowSpan>
				<RCCTooltip
					color="grey"
					text={translate("Stacktrace")}
					position="left"	
				>
					<RCCButton
						color="grey"
						icon={{
							name: "stack"
						}}
						layout="isRounded"
						onClick={() => { modalDescriptionRef.current?.openModal(translate("Stacktrace"), row.efs005Stacktrace, "450px"); }}
					/>
				</RCCTooltip>	
			</RCCGrid>		
		</>
	);

	const colUrl = (row: any) => (
		<>
    	<OverflowSpan>
				<RCCIcon 
					name="infoCircle" 
					color="var(--grey)" 
					style={{ cursor: "pointer", marginRight: "8px"}}
					size={16}
					onClick={() => modalDescriptionRef.current?.openModal(translate("Url"), row.efs005Url)} 
				/>
				{row.efs005Url}
			</OverflowSpan>
		</>
	);

	const colUser = (row: any) => (
		<UserColumn 
			name={row.efa005Nome}
			image={row.efa005Imagem}
		/>
	);

	const groupValueWatch = watchFilters("group");

	return (
		<>

			<PageTitle
				title={translate("Incidentes")}
				icon={() => <AppIcons name="configs" />}
			>
				<RCCTooltip
					text={translate("Filtros")}
					color={showFilters ? "blue" : "grey"}
				>
					<RCCButton
						icon={{name: "filter", color: "#FFF", size: 16}}
						color={showFilters ? "blue" : "grey"}
						layout="isRounded"
						size="md"
						onClick={() => setShowFilters((prevState) => !prevState)}
					/>
				</RCCTooltip>
			</PageTitle>

			{showFilters && (
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters(
						{ filter: "", group: "", user: "", startDate: null, endDate: null }
					)}
				>
					<RCCGrid
						container
						item
					>
						<RCCGrid
							container
							item
							direction="row"
							spacing={1}
							xs={10}
						>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={4}
								lg={4}
							>
							 <DateTimePickerRange
									control={controlFilters}
									label={translate("Período")}
									labelSeparator={translate("até")}
									nameInit = "startDate"
									nameEnd = "endDate"
									defaultValueEnd={filters.endDate}
									defaultValueInit={filters.startDate}
									type="datePicker"
									setValue={setValueFilters}
								/>
							</RCCGrid>
             
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={4}
								lg={4}
							>
								<LovBusinessGroups 
									control={controlFilters}
									name="group" 
									defaultValue={filters.group ?? ""}
									label={translate("Grupo de Empresas")}
									submit="value and label"
									isClearable
									onChangeCapture={() => {
										setValueFilters("user" , undefined);
									}}
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={4}
								lg={4}
							>
								<LovUsers 
									key={groupValueWatch?.value || groupValueWatch}
									control={controlFilters}
									name="user"
									label={translate("Usuário")}
									defaultValue={filters.user}
									extraParametersOnLoad={{
										group: groupValueWatch?.value || groupValueWatch || ""
									}}
									disabled={!groupValueWatch}
									isClearable
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={4}
								lg={4}
							>
								<RCCInput
									name="filter" label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>
			)}
        
			<Pagination 
				ref={paginationRef}
				dataOrRoute="Incidents/List"  
				filtersData={{
					...filters,
					startDate: filters.startDate ?? "",
					endDate: filters.endDate ?? ""
				}}
				columns={[
					{
						idColumn: "efa001Fantasia",
						nameColumn: translate("Grupo empresarial"),
						xs: 12,
						sm: 2,
						md: 2,
						type: "string"
					},{
						idColumn: "efs005Efa005Id",
						nameColumn: translate("Usuário"),
						xs: 12,
						sm: 2,
						md: 2,
						type: "string",
						cellRenderer: colUser
					},
					{
						idColumn: "efs005Datahora",
						nameColumn: translate("Data e hora"),
						xs: 12,
						sm: 1,
						md: 1,
						cellRenderer: (row: any) => formatDate(row.efs005Datahora, currentLanguage, true)
					},{
						idColumn: "efs005Url",
						nameColumn: translate("Url"),
						xs: 12,
						sm: 2,
						md: 2,
						type: "string",
						cellRenderer: colUrl
					},{
						idColumn: "efs005Mensagem",
						nameColumn: translate("Mensagem"),
						xs: 12,
						sm: 5,
						md: 5,
						type: "string",
						cellRenderer: colMessage
					}
				]}
			/>

			<ModalDescription 
				ref={modalDescriptionRef} 	
			/>
      
		</>
	);

};

export default Incidents;
